import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../../services/api';

interface PnLGraphModalProps {
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
}

interface PnLDataPoint {
  date: string;
  pnl: number;
  cumulativePnL: number;
}

const PnLGraphModal: React.FC<PnLGraphModalProps> = ({ isOpen, onClose, isAdmin }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PnLDataPoint[]>([]);
  const [period, setPeriod] = useState<'week' | 'month' | '3month' | 'year' | 'alltime'>('month');
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchPnLData();
    }
  }, [isOpen, period, isAdmin]);

  const fetchPnLData = async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const now = new Date();
      let startDate: Date | null = null;
      
      // For "All Time", get the earliest account creation date
      if (period === 'alltime') {
        try {
          if (isAdmin) {
            // For admin, use a very old date to get all trades from all active accounts
            // The backend already filters by active accounts, so this will get all historical data
            startDate = new Date('2020-01-01');
          } else {
            // For regular users, get their earliest account creation date
            const accountsResponse = await api.get('/accounts');
            const accounts = accountsResponse.data?.data || [];
            
            if (Array.isArray(accounts) && accounts.length > 0) {
              const accountDates = accounts
                .filter((acc: any) => acc.status === 'ACTIVE' && acc.createdAt)
                .map((acc: any) => new Date(acc.createdAt));
              
              if (accountDates.length > 0) {
                startDate = new Date(Math.min(...accountDates.map(d => d.getTime())));
              } else {
                // No active accounts, use a default old date
                startDate = new Date('2020-01-01');
              }
            } else {
              // No accounts found, use a default old date
              startDate = new Date('2020-01-01');
            }
          }
        } catch (error) {
          console.warn('Failed to fetch account creation dates, using default:', error);
          // Fallback to a very old date if we couldn't get account dates
          startDate = new Date('2020-01-01');
        }
      } else {
        // For specific periods, calculate start date
        startDate = new Date();
        switch (period) {
          case 'week':
            startDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            startDate.setMonth(now.getMonth() - 1);
            break;
          case '3month':
            startDate.setMonth(now.getMonth() - 3);
            break;
          case 'year':
            startDate.setFullYear(now.getFullYear() - 1);
            break;
        }
      }

      // Fetch trades for the period
      // IMPORTANT: Regular users use /trades which filters by userId automatically
      // Admin users use /admin/all-trades which gets all trades from all active accounts
      const params = {
        startDate: startDate.toISOString(),
        limit: 10000
      };
      
      console.log('[PnLModal] Fetching trades:', { isAdmin, params, period });
      
      let response;
      try {
        if (isAdmin) {
          response = await api.get('/admin/all-trades', { params });
        } else {
          response = await api.get('/trades', { params });
        }
      } catch (apiError: any) {
        console.error('[PnLModal] API error:', apiError?.response?.data || apiError);
        throw apiError;
      }
      
      // Handle multiple possible response structures
      let trades: any[] = [];
      if (response.data?.data?.trades) {
        trades = response.data.data.trades;
      } else if (response.data?.trades) {
        trades = response.data.trades;
      } else if (Array.isArray(response.data?.data)) {
        trades = response.data.data;
      } else if (Array.isArray(response.data)) {
        trades = response.data;
      }

      console.log('[PnLModal] Fetched trades:', {
        isAdmin,
        totalTrades: trades.length,
        responseStructure: Object.keys(response.data || {}),
        sampleTrade: trades[0]
      });

      // Group trades by date and calculate P&L
      const pnlByDate: { [date: string]: number } = {};
      
      trades.forEach((trade: any) => {
        // Use filledAt if available, otherwise use createdAt as fallback
        const dateField = trade.filledAt || trade.createdAt;
        
        if (trade.status === 'FILLED' && dateField) {
          const date = new Date(dateField).toISOString().split('T')[0];
          if (date) {
            // Use realizedPnl if available, otherwise calculate from trade data
            let pnl = 0;
            if (trade.realizedPnl !== null && trade.realizedPnl !== undefined) {
              pnl = Number(trade.realizedPnl);
            } else if (trade.side === 'SELL' && trade.filledQuantity && trade.averagePrice) {
              // For SELL trades without realizedPnl, we can't calculate accurately without cost basis
              // But we'll include them with 0 P&L to show trade activity
              pnl = 0;
            }
            pnlByDate[date] = (pnlByDate[date] || 0) + pnl;
          }
        }
      });

      console.log('[PnLModal] Processed P&L by date:', {
        datesWithData: Object.keys(pnlByDate).length,
        totalPnL: Object.values(pnlByDate).reduce((sum, val) => sum + val, 0)
      });

      // Convert to array and calculate cumulative P&L
      const sortedDates = Object.keys(pnlByDate).sort();
      let cumulativePnL = 0;
      const chartData: PnLDataPoint[] = sortedDates.map(date => {
        const pnl = pnlByDate[date] || 0;
        cumulativePnL += pnl;
        return {
          date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          pnl: Number(pnl.toFixed(2)),
          cumulativePnL: Number(cumulativePnL.toFixed(2))
        };
      });

      setData(chartData);
    } catch (error: any) {
      console.error('Failed to fetch P&L data:', error);
      setData([]);
      const msg = error?.response?.data?.error?.message 
        || error?.response?.data?.error 
        || (typeof error?.message === 'string' ? error.message : null);
      setFetchError(msg || 'Failed to load P&L data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-neutral-900 rounded-xl border border-neutral-700 max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-700">
          <div>
            <h2 className="text-2xl font-bold text-white">Profit & Loss History</h2>
            <p className="text-sm text-neutral-400 mt-1">Track your trading performance over time</p>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-white transition-colors p-2 hover:bg-neutral-800 rounded-lg"
            aria-label="Close modal"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Period Selector */}
        <div className="px-6 py-4 border-b border-neutral-800">
          <div className="flex gap-2 flex-wrap">
            {[
              { value: 'week', label: '1 Week' },
              { value: 'month', label: '1 Month' },
              { value: '3month', label: '3 Months' },
              { value: 'year', label: '1 Year' },
              { value: 'alltime', label: 'All Time' }
            ].map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setPeriod(value as any)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  period === value
                    ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-600/30'
                    : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700 hover:text-white'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Chart */}
        <div className="p-6">
          {loading ? (
            <div className="h-96 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
            </div>
          ) : fetchError ? (
            <div className="h-96 flex flex-col items-center justify-center text-neutral-400">
              <svg className="h-16 w-16 mb-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-lg font-medium text-white">Unable to load P&L data</p>
              <p className="text-sm mt-1 max-w-sm text-center">{fetchError}</p>
              <button
                onClick={fetchPnLData}
                className="mt-4 px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-500 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : data.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  stroke="#9CA3AF"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="#9CA3AF"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `$${value}`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#F3F4F6'
                  }}
                  formatter={(value: any) => [`$${Number(value).toFixed(2)}`, '']}
                  labelStyle={{ color: '#9CA3AF' }}
                />
                <Legend 
                  wrapperStyle={{ color: '#9CA3AF' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="pnl" 
                  stroke="#F59E0B" 
                  name="Daily P&L"
                  strokeWidth={2}
                  dot={{ fill: '#F59E0B', r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="cumulativePnL" 
                  stroke="#06B6D4" 
                  name="Cumulative P&L"
                  strokeWidth={3}
                  dot={{ fill: '#06B6D4', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-96 flex flex-col items-center justify-center text-neutral-400">
              <svg className="h-16 w-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-lg font-medium">No P&L data available</p>
              <p className="text-sm mt-1">Start trading to see your profit & loss history</p>
            </div>
          )}
        </div>

        {/* Footer Stats */}
        {!loading && data.length > 0 && (() => {
          const lastData = data[data.length - 1];
          if (!lastData) return null;
          return (
            <div className="px-6 py-4 border-t border-neutral-800 bg-neutral-900/50">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-neutral-400 mb-1">Total P&L</p>
                  <p className={`text-lg font-bold ${lastData.cumulativePnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    ${lastData.cumulativePnL.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-400 mb-1">Best Day</p>
                  <p className="text-lg font-bold text-green-400">
                    ${Math.max(...data.map(d => d.pnl)).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-400 mb-1">Worst Day</p>
                  <p className="text-lg font-bold text-red-400">
                    ${Math.min(...data.map(d => d.pnl)).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default PnLGraphModal;
